import React from 'react'
import { observer } from 'mobx-react-lite'

import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'

import { ExpandMore } from '@mxenabled/mx-icons'

import {
  ToggleButtonListItem,
  ToggleListItem,
  useGlobalCopyStore,
  useSettingsStore,
  useUserStore,
} from '../../../common'
import { type DetailedNotificationProfile } from '../../../core'

interface BudgetNotificationProfileProps {
  profile: DetailedNotificationProfile
}

export const NotificationProfile = observer(({ profile }: BudgetNotificationProfileProps) => {
  const { settings: copy } = useGlobalCopyStore()
  const {
    clientCommunicationProfile: {
      email_notifications_enabled: showEmail,
      push_notifications_enabled: showPush,
      sms_notifications_enabled: showSms,
    },
    updateNotificationProfile,
    userCommunicationsProfile: {
      opted_out_of_email_notifications: emailDisabled,
      opted_out_of_push_notifications: pushDisabled,
      opted_out_of_sms_notifications: smsDisabled,
    },
  } = useSettingsStore()

  const { user: userDetails } = useUserStore()

  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleProfileToggle = async () => {
    await handleUpdateProfile({ ...profile, is_enabled: !profile.is_enabled })

    if (profile.is_enabled) {
      setIsExpanded(false)
    }
  }

  const handleUpdateProfile = async (updatedProfile: DetailedNotificationProfile) => {
    await updateNotificationProfile(updatedProfile)
  }

  const hasDeliveryMethods = showEmail || showSms || showPush

  return (
    <List key={profile.guid} sx={{ bgcolor: 'background.paper' }}>
      <ToggleButtonListItem
        endIcon={
          hasDeliveryMethods ? (
            <ExpandMore
              sx={{
                transition: 'transform 0.3s ease-in-out',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          ) : undefined
        }
        isChecked={profile.is_enabled}
        isDisabled={!profile.is_enabled}
        key={profile.guid}
        onClick={() => setIsExpanded(!isExpanded)}
        onToggle={() => handleProfileToggle()}
        primaryText={profile.label}
        secondaryText={profile.is_enabled ? profile.deliveryMethodsLabel : undefined}
      />
      {hasDeliveryMethods && (
        <Collapse in={isExpanded}>
          <List sx={{ bgcolor: 'background.paper' }}>
            {showEmail && (
              <ToggleListItem
                isChecked={!emailDisabled && profile.email_channel && !!userDetails.email}
                isDisabled={emailDisabled || !userDetails.email}
                onToggle={() =>
                  handleUpdateProfile({
                    ...profile,
                    email_channel: !profile.email_channel,
                  })
                }
                primaryText={copy.delivery_method_email_label}
              />
            )}

            {showEmail && (showSms || showPush) && <Divider sx={{ ml: 48 }} />}

            {showSms && (
              <ToggleListItem
                isChecked={!smsDisabled && profile.sms_channel && !!userDetails.phone}
                isDisabled={smsDisabled || !userDetails.phone}
                onToggle={() =>
                  handleUpdateProfile({
                    ...profile,
                    sms_channel: !profile.sms_channel,
                  })
                }
                primaryText={copy.delivery_method_sms_label}
              />
            )}

            {showSms && showPush && <Divider sx={{ ml: 48 }} />}

            {showPush && (
              <ToggleListItem
                isChecked={!pushDisabled && profile.push_channel}
                isDisabled={pushDisabled}
                onToggle={() =>
                  handleUpdateProfile({
                    ...profile,
                    push_channel: !profile.push_channel,
                  })
                }
                primaryText={copy.delivery_method_push_label}
              />
            )}
          </List>
        </Collapse>
      )}
      <Divider />
    </List>
  )
})
